import { FeatureDependencies } from "./FeatureDependencies";

function CircuitExtraction() {
  return (
    <section id="CircuitExtraction">
      <h2>Circuit Extraction</h2>
      <p>
        High-level features depend upon earlier ones that, when ablated (i.e., removed), alter the
        behavior of the model. To understand how an LLM processes a specific sequence, we work
        backwards from active “downstream” features and extract a graph showing key “upstream”
        dependencies. In the circuits that we extract, the top most layer represents the embeddings
        that are passed through the first transformer block. The last layer represents the
        activations after passing through the last transformer block.
      </p>
      <FeatureDependencies />
      <p>
        When measuring the impact one feature may have on another, we iteratively ablate the
        upstream feature across a range of input indices so that we can isolate the effects of an
        upstream feature to its activation on a specific token. If the ablation of a feature at a
        specific index yields lower activation values for another feature, we mark the dependency
        and add it to the graph.
      </p>
      <h3 id="FeaturePruning">Feature Pruning</h3>
      <p>
        The circuits that we extract are enhanced through feature pruning. Various types of
        thresholds are used to favor the inclusion of “important” features and exclude irrelevant or
        uninterpretable ones.
      </p>
      <h4>Activation Thresholds</h4>
      <p>
        The most relevant features tend to have the highest activation values. Features with lower
        activation values do not reliably fire on samples that relate to the sequence being
        processed. To filter out these types of features, we set a fixed minimum activation
        threshold on a per-layer basis.
      </p>
      <h4>Firing Frequency Thresholds</h4>
      <p>
        Features that fire infrequently often respond to patterns that are easier to understand.
        Features that fire frequently often have ambiguous meaning, which can reduce the
        interpretability of a graph. For each layer before the final layer, we filter out features
        that fall within the top 0.1% of firing frequency.
      </p>
      <h4>Ablation Thresholds</h4>
      <p>
        Upstream features are selected based on their ablation impact on downstream features, with
        thresholds set using means and standard deviations. When choosing the “root” features to
        start with in the final layer, we measure each feature's ablation impact on the output
        logits generated by the LLM. The top two or three features are selected for inclusion in the
        final circuit.
      </p>
    </section>
  );
}

export { CircuitExtraction };
